import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Paper,
  IconButton,
  CircularProgress,
  Alert,
  Typography
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useMutation, useQueryClient } from 'react-query';
import { mutation } from '../../../utils/api';
import { ApiRoutes } from '../../../constants/routes';
import { useColors } from '../../../constants/colors';
import ImageIcon from '@mui/icons-material/Image';
import LinkIcon from '@mui/icons-material/Link';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import { Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import PostMusic from '../PostMusic';
import PostAsSelect, { authorFields, usePostAs } from '../PostAs';

// music: the track / album / playlist / artist this post shares
// ({ type, id, name, act_name, image, slug }) - from "Post to Feed"
// actId / organization ({ kind, id }): post only as that artist, label or
// publisher (its own dashboard); otherwise the person chooses
const QuickPost = ({ actId, organization, music, onSuccess, compact = false, buttonStyles }) => {
  const Colors = useColors();
  const author = usePostAs(actId ? `act-${actId}` : (organization ? `${organization.kind}-${organization.id}` : null));
  const theme = useTheme();
  const [textContent, setTextContent] = useState('');
  const [postType, setPostType] = useState('status');
  const [images, setImages] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkTitle, setLinkTitle] = useState('');
  const [linkDescription, setLinkDescription] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState(null);
  const queryClient = useQueryClient();

  const createPostMutation = useMutation({
    mutationFn: (data) => mutation(ApiRoutes.Posts, data, 'POST'),
    onSuccess: () => {
      queryClient.invalidateQueries(['posts']);
      queryClient.invalidateQueries(['post-feed']);
      setTextContent('');
      setPostType('status');
      setImages([]);
      setLinkUrl('');
      setLinkTitle('');
      setLinkDescription('');
      setExpanded(false);
      setError(null);
      if (onSuccess) onSuccess();
    },
    onError: (err) => {
      setError(err.message || 'Failed to create post');
    }
  });

  const uploadImageMutation = useMutation({
    mutationFn: async (file) => {
      const formData = new FormData();
      formData.append('image', file);
      
      const token = localStorage.getItem('token');
      const response = await fetch(ApiRoutes.UploadPostImage, {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Token ${token}`,
        },
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Upload failed: ${response.status}`);
      }
      
      const data = await response.json();
      return data.image_url;
    },
    onError: (err) => {
      setError(err.message || 'Failed to upload image');
      setUploadingImages(false);
    }
  });

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setError(null);
    setUploadingImages(true);
    
    try {
      const uploadedUrls = [];
      for (const file of files) {
        if (!file.type.startsWith('image/')) {
          setError('Please select valid image files only');
          setUploadingImages(false);
          return;
        }
        
        const maxSize = 10 * 1024 * 1024; // 10MB
        if (file.size > maxSize) {
          setError(`Image "${file.name}" is too large. Maximum size is 10MB.`);
          setUploadingImages(false);
          return;
        }
        
        const imageUrl = await uploadImageMutation.mutateAsync(file);
        uploadedUrls.push(imageUrl);
      }
      
      setImages([...images, ...uploadedUrls]);
      setUploadingImages(false);
    } catch (err) {
      setUploadingImages(false);
    }
    
    e.target.value = '';
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);

    if (postType === 'photo' && images.length === 0) {
      setError('Photo posts must have at least one image');
      return;
    }

    if (postType === 'link' && !linkUrl) {
      setError('Link posts must have a URL');
      return;
    }

    if (!music && postType === 'status' && !textContent.trim()) {
      setError('Please enter some content');
      return;
    }

    const data = {
      post_type: postType,
      text_content: textContent.trim() || '',
      images: images.length > 0 ? images : [],
      link_url: linkUrl || '',
      link_title: linkTitle || '',
      link_description: linkDescription || '',
      is_public: true,
    };

    Object.assign(data, authorFields(author.postAs));
    if (music) {
      data.post_type = 'music';
      data.music_type = music.type;
      data.music_id = music.id;
    }

    createPostMutation.mutate(data);
  };

  if (compact) {
    // Compact version - just a button that expands
    if (!expanded) {
      return (
        <Button
          fullWidth
          variant="outlined"
          onClick={() => setExpanded(true)}
          startIcon={<SendIcon />}
          sx={buttonStyles || {
            border: '1px solid rgba(255, 255, 255, 0.2)',
            color: 'rgba(255, 255, 255, 0.7)',
            borderRadius: 2,
            py: 1.5,
            '&:hover': {
              border: '1px solid rgba(255, 255, 255, 0.4)',
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
            },
          }}
        >
          Share something...
        </Button>
      );
    }
  }

  return (
    <Paper
      sx={{
        p: 2,
        background: 'rgba(255, 255, 255, 0.03)',
        borderRadius: 2,
        border: '1px solid rgba(255, 255, 255, 0.08)',
        mb: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 0.5 }}>
            {author.postAs !== 'me'
              ? `Post as ${author.options.find((option) => option.key === author.postAs)?.label || (actId ? 'the artist' : 'it')}`
              : (music ? 'Share this with your followers' : 'Share something with your followers')}
          </Typography>
        </Box>
        {compact && (
          <IconButton
            size="small"
            onClick={() => {
              setExpanded(false);
              setTextContent('');
              setPostType('status');
              setImages([]);
              setLinkUrl('');
              setLinkTitle('');
              setLinkDescription('');
            }}
            sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <PostAsSelect {...author} />
        {music && <PostMusic music={music} />}

        {!music && (
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Post Type</InputLabel>
          <Select
            value={postType}
            onChange={(e) => setPostType(e.target.value)}
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.2)',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.3)',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
            }}
          >
            <MenuItem value="status">Status (Text)</MenuItem>
            <MenuItem value="photo">Photo</MenuItem>
            <MenuItem value="link">Link</MenuItem>
          </Select>
        </FormControl>
        )}

        <TextField
          fullWidth
          multiline
          rows={compact ? 3 : 4}
          placeholder={music ? 'Say something about it (optional)' : "What's on your mind?"}
          value={textContent}
          onChange={(e) => setTextContent(e.target.value)}
          disabled={createPostMutation.isLoading}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              '& fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.2)',
              },
              '&:hover fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.3)',
              },
              '&.Mui-focused fieldset': {
                borderColor: theme.palette.primary.main,
              },
            },
            '& .MuiInputBase-input::placeholder': {
              color: 'rgba(255, 255, 255, 0.4)',
            },
          }}
        />

        {postType === 'photo' && (
          <Box sx={{ mb: 2 }}>
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="image-upload-quick"
              multiple
              type="file"
              onChange={handleImageUpload}
              disabled={uploadingImages}
            />
            <label htmlFor="image-upload-quick">
              <Button
                component="span"
                variant="outlined"
                disabled={uploadingImages}
                startIcon={<ImageIcon />}
                sx={{
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                  mb: 2,
                  '&:disabled': {
                    borderColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'rgba(255, 255, 255, 0.5)',
                  },
                }}
              >
                {uploadingImages ? 'Uploading...' : 'Upload Images'}
              </Button>
            </label>
            {images.length > 0 && (
              <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {images.map((imgUrl, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      position: 'relative',
                      width: 100,
                      height: 100,
                      borderRadius: 1,
                      overflow: 'hidden',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                    }}
                  >
                    <Box
                      component="img"
                      src={imgUrl}
                      alt={`Upload ${idx + 1}`}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => setImages(images.filter((_, i) => i !== idx))}
                      sx={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        backgroundColor: 'rgba(0, 0, 0, 0.6)',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        },
                        width: 24,
                        height: 24,
                      }}
                    >
                      ×
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}

        {postType === 'link' && (
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              label="Link URL"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              required
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  '& fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: Colors.mainYellow,
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                },
              }}
            />
            <TextField
              fullWidth
              label="Link Title (optional)"
              value={linkTitle}
              onChange={(e) => setLinkTitle(e.target.value)}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  '& fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: Colors.mainYellow,
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                },
              }}
            />
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Link Description (optional)"
              value={linkDescription}
              onChange={(e) => setLinkDescription(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  '& fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: Colors.mainYellow,
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                },
              }}
            />
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton
              size="small"
              onClick={() => setPostType('photo')}
              sx={{
                color: postType === 'photo' ? theme.palette.primary.main : theme.palette.text.secondary,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
              title="Add photo"
            >
              <ImageIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => setPostType('link')}
              sx={{
                color: postType === 'link' ? theme.palette.primary.main : theme.palette.text.secondary,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
              title="Add link"
            >
              <LinkIcon fontSize="small" />
            </IconButton>
          </Box>

          <Button
            type="submit"
            variant="contained"
            disabled={createPostMutation.isLoading || !textContent.trim()}
            startIcon={
              createPostMutation.isLoading ? (
                <CircularProgress size={16} sx={{ color: theme.palette.primary.contrastText }} />
              ) : (
                <SendIcon />
              )
            }
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
              },
              '&:disabled': {
                backgroundColor: `${theme.palette.primary.main}30`,
                color: `${theme.palette.primary.contrastText}50`,
              },
            }}
          >
            Post
          </Button>
        </Box>
      </form>
    </Paper>
  );
};

export default QuickPost;

